import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { assigneeDisplayName, useSystemUsers } from '@Purchasing/Hooks/useSystemUsers/useSystemUsers'
import { Check, ChevronDown, Loader2, Search, UserCheck, UserX } from 'lucide-react'
import { useState } from 'react'
import { UserAvatar } from './UserAvatar'

export interface AssigneeValue {
    uid?: string
    mid?: string
    name?: string
    mail?: string
}

interface AssigneePickerProps {
    value?: AssigneeValue | null
    /** id = uid do usuário; null = remover o responsável. */
    onChange: (id: string | null) => void | Promise<unknown>
    disabled?: boolean
    saving?: boolean
    className?: string
    /** Na criação o responsável é opcional e não precisa de "remover". */
    allowClear?: boolean
}

/** Escolhe o responsável entre os usuários do sistema (com "Atribuir a mim"). */
export const AssigneePicker = ({ value, onChange, disabled, saving, className, allowClear = true }: AssigneePickerProps) => {
    const { users, loading, currentUserId } = useSystemUsers()
    const [open, setOpen] = useState(false)
    const [query, setQuery] = useState('')
    const isCurrent = (id: string) => id === value?.uid || id === value?.mid
    const currentName = assigneeDisplayName(value, users)
    const me = users.find((user) => user.id === currentUserId)
    const visible = users.filter((user) =>
        !query.trim() || `${user.name} ${user.mail ?? ''}`.toLowerCase().includes(query.trim().toLowerCase())
    )

    const pick = async (id: string | null) => {
        setOpen(false)
        setQuery('')
        await onChange(id)
    }

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    disabled={disabled || saving}
                    aria-label="Responsável"
                    data-testid="assignee-picker"
                    className={cn(
                        'inline-flex h-9 min-w-[200px] items-center gap-2 rounded-full border border-border bg-secondary px-2 pr-3 text-sm disabled:cursor-not-allowed disabled:opacity-60',
                        className
                    )}
                >
                    {currentName ? <UserAvatar name={currentName} className="h-6 w-6 text-[10px]" /> : <UserX className="ml-1 h-4 w-4 text-muted-foreground" strokeWidth={1.5} />}
                    <span className={cn('truncate', !currentName && 'text-muted-foreground')}>{currentName || 'Sem responsável'}</span>
                    {saving ? <Loader2 className="ml-auto h-4 w-4 animate-spin" /> : <ChevronDown className="ml-auto h-4 w-4 shrink-0 opacity-60" />}
                </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-72 p-2">
                {me && !isCurrent(me.id) && (
                    <button type="button" data-testid="assign-to-me" onClick={() => pick(me.id)}
                        className="mb-1 flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm font-semibold text-brand-deep hover:bg-accent">
                        <UserCheck className="h-4 w-4" strokeWidth={1.5} />Atribuir a mim
                    </button>
                )}
                <div className="relative mb-1">
                    <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" strokeWidth={1.5} />
                    <Input aria-label="Buscar usuário" placeholder="Buscar usuário" className="h-8 pl-8 text-sm" value={query} onChange={(e) => setQuery(e.target.value)} autoFocus />
                </div>
                <div className="max-h-64 overflow-y-auto" role="listbox" aria-label="Usuários">
                    {loading && <p className="px-2 py-1.5 text-sm text-muted-foreground">Carregando usuários…</p>}
                    {visible.map((user) => (
                        <button key={user.id} type="button" role="option" aria-label={user.name} aria-selected={isCurrent(user.id)} onClick={() => pick(user.id)}
                            className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-accent">
                            <UserAvatar name={user.name} className="h-6 w-6 text-[10px]" />
                            <span className="truncate">{user.name}{user.id === currentUserId && <span className="text-muted-foreground"> (você)</span>}</span>
                            <Check className={cn('ml-auto h-4 w-4 shrink-0', isCurrent(user.id) ? 'opacity-100' : 'opacity-0')} />
                        </button>
                    ))}
                    {!loading && !visible.length && <p className="px-2 py-1.5 text-sm text-muted-foreground">Nenhum usuário encontrado.</p>}
                </div>
                {allowClear && currentName && (
                    <button type="button" data-testid="assignee-clear" onClick={() => pick(null)}
                        className="mt-1 flex w-full items-center gap-2 rounded-md border-t border-border px-2 pt-2 text-left text-sm text-od-secondary-600 hover:bg-accent">
                        <UserX className="h-4 w-4" strokeWidth={1.5} />Remover responsável
                    </button>
                )}
            </PopoverContent>
        </Popover>
    )
}
